import { Injectable } from '@angular/core';
import { ES_CONSTANTS, ITokenScope } from '@portal/entity-services/interfaces';
import { VuiHttpService } from '@portal/shared/vui-http';
import { unionBy } from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TokenScopeService {
  private tokenScopesSubject: BehaviorSubject<ITokenScope[]> = new BehaviorSubject(
    [] as ITokenScope[],
  );
  private linkedTokenScopesSubject: BehaviorSubject<ITokenScope[]> = new BehaviorSubject(
    [] as ITokenScope[],
  );

  private currentEntityTokenScopesSubject: BehaviorSubject<ITokenScope[]> = new BehaviorSubject(
    [] as ITokenScope[],
  );

  constructor(private vuiHttpService: VuiHttpService) {}

  get tokenScopes$(): Observable<ITokenScope[]> {
    return this.tokenScopesSubject.asObservable();
  }

  get filteredTokenScopes$(): Observable<ITokenScope[]> {
    return combineLatest([this.tokenScopes$, this.linkedTokenScopes$]).pipe(
      map(([tokenScopes, linkedTokenScopes]) => {
        return tokenScopes.filter(
          (tokenScope) =>
            !linkedTokenScopes.some(
              (linkedTokenScope) => linkedTokenScope.tokenScopeUid === tokenScope.tokenScopeUid,
            ),
        );
      }),
    );
  }

  get filteredTokenScopesExcludingCurrentEntity$(): Observable<ITokenScope[]> {
    return combineLatest([this.filteredTokenScopes$, this.currentEntityTokenScopes$]).pipe(
      map(([filterTokenScopes, currentEntityTokenScopes]) => {
        return filterTokenScopes.filter(
          (filterTokenScope) =>
            !currentEntityTokenScopes.some(
              (currentEntityTokenScope) =>
                currentEntityTokenScope.tokenScopeUid === filterTokenScope.tokenScopeUid,
            ),
        );
      }),
    );
  }

  get linkedTokenScopes$(): Observable<ITokenScope[]> {
    return this.linkedTokenScopesSubject.asObservable();
  }

  get currentEntityTokenScopes$(): Observable<ITokenScope[]> {
    return this.currentEntityTokenScopesSubject.asObservable();
  }

  getAll(
    merchantCompanyEntityUid: string,
    isCurrentEntity: boolean = false,
  ): Observable<ITokenScope[]> {
    return this.vuiHttpService
      .get<ITokenScope[]>(
        `${ES_CONSTANTS.ENTITY_SERVICE.ORGANISATION}${merchantCompanyEntityUid}/tokenScopes`,
      )
      .pipe(
        tap((tokenScopes: ITokenScope[]) => {
          if (isCurrentEntity) {
            this.setCurrentEntityTokenScopes(tokenScopes);
          } else {
            this.setTokenScopes([...this.tokenScopesSubject.getValue(), ...tokenScopes]);
          }
        }),
      );
  }

  getAllLinked(entityUid: string): Observable<ITokenScope[]> {
    return this.vuiHttpService
      .get<ITokenScope[]>(
        `${ES_CONSTANTS.ENTITY_SERVICE.ORGANISATION}${entityUid}/allowedTokenScopes`,
      )
      .pipe(
        tap((tokenScopes: ITokenScope[]) => {
          this.setLinkedTokenScopes(tokenScopes);
        }),
      );
  }

  addTokenScope(
    merchantCompanyEntityUid: string,
    tokenScope: ITokenScope,
  ): Observable<ITokenScope> {
    return this.vuiHttpService
      .post<ITokenScope>(
        `${ES_CONSTANTS.ENTITY_SERVICE.ORGANISATION}${merchantCompanyEntityUid}/tokenScopes`,
        tokenScope,
      )
      .pipe(
        tap((addedTokenScope: ITokenScope) => {
          this.setTokenScopes([...this.tokenScopesSubject.getValue(), addedTokenScope]);
          this.setCurrentEntityTokenScopes([
            ...this.currentEntityTokenScopesSubject.getValue(),
            addedTokenScope,
          ]);
        }),
      );
  }

  updateTokenScope(newTokenScope: ITokenScope): Observable<ITokenScope> {
    return this.vuiHttpService
      .put<ITokenScope>(
        `${ES_CONSTANTS.ENTITY_SERVICE.ROOT}/tokenScopes/${newTokenScope.tokenScopeUid}`,
        newTokenScope,
      )
      .pipe(
        tap((updatedTokenScope: ITokenScope) => {
          const updatedTokenScopes = this.tokenScopesSubject
            .getValue()
            .map((tokenScope) =>
              tokenScope.tokenScopeUid === newTokenScope.tokenScopeUid
                ? updatedTokenScope
                : tokenScope,
            );
          this.setTokenScopes(updatedTokenScopes);

          const updatedCurrentEntityTokenScopes = this.currentEntityTokenScopesSubject
            .getValue()
            .map((tokenScope) =>
              tokenScope.tokenScopeUid === newTokenScope.tokenScopeUid
                ? updatedTokenScope
                : tokenScope,
            );
          this.setCurrentEntityTokenScopes(updatedCurrentEntityTokenScopes);
        }),
      );
  }

  deleteTokenScope(tokenScopeUid: string): Observable<ITokenScope> {
    return this.vuiHttpService
      .delete<ITokenScope>(`${ES_CONSTANTS.ENTITY_SERVICE.ROOT}/tokenScopes/${tokenScopeUid}`)
      .pipe(
        tap(() => {
          this.setLinkedTokenScopes(
            this.linkedTokenScopesSubject
              .getValue()
              .filter((linked) => linked.tokenScopeUid !== tokenScopeUid),
          );
          const updatedTokenScopes = this.tokenScopesSubject
            .getValue()
            .filter((tokenScope) => tokenScope.tokenScopeUid !== tokenScopeUid);
          this.setTokenScopes(updatedTokenScopes);
          const updatedCurrentEntityTokenScopes = this.currentEntityTokenScopesSubject
            .getValue()
            .filter((tokenScope) => tokenScope.tokenScopeUid !== tokenScopeUid);
          this.setCurrentEntityTokenScopes(updatedCurrentEntityTokenScopes);
        }),
      );
  }

  unlinkTokenScope(
    entityUid: string,
    linkedTokenScopeUid: ITokenScope['tokenScopeUid'],
  ): Observable<any> {
    return this.vuiHttpService
      .delete<any>(
        `${ES_CONSTANTS.ENTITY_SERVICE.ORGANISATION}${entityUid}/allowedTokenScopes/${linkedTokenScopeUid}`,
      )
      .pipe(
        tap(() => {
          this.setLinkedTokenScopes(
            this.linkedTokenScopesSubject
              .getValue()
              .filter((linked) => linked.tokenScopeUid !== linkedTokenScopeUid),
          );
          this.setTokenScopes(this.tokenScopesSubject.getValue());
        }),
      );
  }

  linkTokenScopes(entityUid: string, tokenScopes: ITokenScope[]): Observable<any> {
    return this.vuiHttpService
      .post<any>(`${ES_CONSTANTS.ENTITY_SERVICE.ORGANISATION}${entityUid}/allowedTokenScopes`, {
        tokenScopeUids: tokenScopes.map((v) => v.tokenScopeUid),
      })
      .pipe(
        tap(() => {
          this.setLinkedTokenScopes(
            unionBy(this.linkedTokenScopesSubject.getValue(), tokenScopes, 'tokenScopeUid'),
          );
          this.setTokenScopes(this.tokenScopesSubject.getValue());
        }),
      );
  }

  clearAll(): void {
    this.setTokenScopes([]);
    this.setLinkedTokenScopes([]);
    this.setCurrentEntityTokenScopes([]);
  }

  nonMatchingLinkedTokenScopes(): Observable<ITokenScope[]> {
    return combineLatest([this.currentEntityTokenScopes$, this.linkedTokenScopes$]).pipe(
      map(([currentEntityTokenScopes, linkedTokenScopes]) =>
        linkedTokenScopes.filter(
          (linkedTokenScope) =>
            !currentEntityTokenScopes.some(
              (currentEntityTokenScope) =>
                currentEntityTokenScope.tokenScopeUid === linkedTokenScope.tokenScopeUid,
            ),
        ),
      ),
    );
  }

  private setTokenScopes(tokenScopes: ITokenScope[]): void {
    this.tokenScopesSubject.next(tokenScopes);
  }

  private setLinkedTokenScopes(linkedTokenScopes: ITokenScope[]): void {
    this.linkedTokenScopesSubject.next(linkedTokenScopes);
  }

  private setCurrentEntityTokenScopes(tokenScopes: ITokenScope[]): void {
    this.currentEntityTokenScopesSubject.next(tokenScopes);
  }
}
